import type {
  CreateEmployeeRequest,
  CurrentShift,
  Employee,
  EmployeeStats,
  EmployeeFormMetadata,
  UpdateEmployeeRequest,
} from '@mervonn/shared-types';
import { apiBlob, apiList, apiRequest } from '../../lib/api/client';
import { properCase } from '../../lib/format';

/**
 * `GET /employees` gained real server-side paging/search in Stage 4 Phase 0
 * (`employees.service.ts#list`, wired into the frontend in Phase 2 — see
 * `listPaged` below for the real `EmployeesListPage` UI). This unpaginated
 * `list()` stays as the "everyone, for a dropdown/lookup" convenience every
 * OTHER screen (Leave, Attendance, Payroll, Reports, device command
 * dialogs) already depends on — those need the full roster, not one page,
 * so they keep asking for `per_page: MAX_LIST` explicitly rather than
 * silently getting truncated to a default page size.
 */
const MAX_LIST = 100;

export interface ListEmployeesQuery {
  page?: number;
  per_page?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'on_leave';
  /** `true` lists only archived employees; `false` (the default view) hides them. */
  archived?: 'true' | 'false';
  branch_id?: string;
  country_code?: string;
  shift_id?: string;
  /** `true` lists only people created from a terminal and not yet completed. */
  details_pending?: 'true';
  /** `true` lists only people who may have been created twice. */
  possible_duplicate?: 'true';
}

/** One problem in an import file; `row` is the spreadsheet row (1 = the header). */
export interface ImportRowError {
  row: number;
  field: string;
  message: string;
}

/** `POST /employees/import` (Re-design T09). `created` is present on a commit. */
export interface ImportResult {
  mode: 'dry_run' | 'commit';
  rows: number;
  valid: number;
  created?: number;
  errors: ImportRowError[];
}

export const employeesApi = {
  list: () => apiRequest<Employee[]>('/employees', { query: { per_page: MAX_LIST } }),
  // Stage 4 Phase 2 — real pagination for `EmployeesListPage` itself.
  listPaged: (query: ListEmployeesQuery = {}) =>
    apiList<Employee>('/employees', { query: query as Record<string, string | number | undefined> }),
  get: (id: string) => apiRequest<Employee>(`/employees/${id}`),
  formMetadata: () => apiRequest<EmployeeFormMetadata>('/employees/form-metadata'),
  create: (body: CreateEmployeeRequest) =>
    apiRequest<Employee>('/employees', { method: 'POST', body }),
  update: (id: string, body: UpdateEmployeeRequest) =>
    apiRequest<Employee>(`/employees/${id}`, { method: 'PATCH', body }),
  importCsv: (file: File, mode: 'dry_run' | 'commit') => {
    const form = new FormData();
    form.append('file', file);
    return apiRequest<ImportResult>('/employees/import', { method: 'POST', query: { mode }, body: form });
  },
  importTemplate: () => apiBlob('/employees/import/template'),
  stats: (branchId?: string) => apiRequest<EmployeeStats>('/employees/stats', { query: { branch_id: branchId } }),
  currentShift: (id: string) => apiRequest<CurrentShift | null>(`/employees/${id}/current-shift`),
  /** Re-pushes the user record (never a template) to every confirmed device. */
  syncAllDevices: (id: string) =>
    apiRequest<{ status: 'pending'; command_log_ids: string[]; skipped: { device_id: string; reason: string }[] }>(
      `/employees/${id}/mappings/sync-all`,
      { method: 'POST' },
    ),
  archive: (id: string) => apiRequest<Employee>(`/employees/${id}/archive`, { method: 'POST' }),
  restore: (id: string) => apiRequest<Employee>(`/employees/${id}/restore`, { method: 'POST' }),
  deactivate: (id: string) =>
    apiRequest<Employee>(`/employees/${id}/deactivate`, { method: 'PATCH' }),
};

export function employeeName(e: Pick<Employee, 'firstName' | 'lastName' | 'employeeNumber'>): string {
  const name = `${e.firstName} ${e.lastName}`.trim();
  return name ? properCase(name) : e.employeeNumber;
}

export function hasSalary(e: Employee): e is Employee & { salaryAmount: string } {
  return typeof e.salaryAmount === 'string';
}
